import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  ApproveSymbolRequestDto,
  BulkApproveResultDto,
  CreateGlobalSymbolDto,
  GlobalSymbolDatatableInput,
  GlobalSymbolDto,
  GlobalSymbolFormDataDto,
  PendingSymbolRequestDto,
  SymbolRequestHistoryDatatableInput,
  SymbolRequestRowDto,
  UpdateGlobalSymbolDto,
} from '@/api/types/globalSymbols';

// Routes from ABP conventional-controller mapping.
//
// Finkit.Host.GlobalSymbols.GlobalSymbolsAppService
// (JS proxy finkit.host.globalSymbols.globalSymbols.*):
//   GetListForDataTableAsync -> GET /api/app/global-symbols/for-data-table
//   GetFormDataAsync         -> GET /api/app/global-symbols/form-data
//   CreateAsync              -> POST /api/app/global-symbols
//   UpdateAsync(id, input)   -> PUT  /api/app/global-symbols/{id}
const SYMBOLS_BASE = '/api/app/global-symbols';

// Finkit.Host.GlobalSymbols.SymbolRequestsAppService
// (JS proxy finkit.host.globalSymbols.symbolRequests.*):
//   GetSymbolRequestHistoryForDataTableAsync -> GET  /api/app/symbol-requests/symbol-request-history-for-data-table
//   GetPendingSymbolRequestsAsync            -> GET  /api/app/symbol-requests/pending-symbol-requests
//   ApproveSymbolRequestAsync(id, input)     -> POST /api/app/symbol-requests/approve-symbol-request/{requestId}
//   ApproveAllAsync(requests)                -> POST /api/app/symbol-requests/approve-all
//   RejectSymbolRequestAsync(id)             -> POST /api/app/symbol-requests/reject-symbol-request/{requestId}
const REQUESTS_BASE = '/api/app/symbol-requests';

export const globalSymbolsApi = {
  async getListForDataTable(
    input: GlobalSymbolDatatableInput,
  ): Promise<PagedResult<GlobalSymbolDto>> {
    const { data } = await http.get<PagedResult<GlobalSymbolDto>>(`${SYMBOLS_BASE}/for-data-table`, {
      params: input,
    });
    return data;
  },

  async getFormData(): Promise<GlobalSymbolFormDataDto> {
    const { data } = await http.get<GlobalSymbolFormDataDto>(`${SYMBOLS_BASE}/form-data`);
    return data;
  },

  async create(input: CreateGlobalSymbolDto): Promise<GlobalSymbolDto> {
    const { data } = await http.post<GlobalSymbolDto>(SYMBOLS_BASE, input);
    return data;
  },

  async update(id: string, input: UpdateGlobalSymbolDto): Promise<GlobalSymbolDto> {
    const { data } = await http.put<GlobalSymbolDto>(`${SYMBOLS_BASE}/${id}`, input);
    return data;
  },
};

export const symbolRequestsApi = {
  async getHistoryForDataTable(
    input: SymbolRequestHistoryDatatableInput,
  ): Promise<PagedResult<SymbolRequestRowDto>> {
    const { data } = await http.get<PagedResult<SymbolRequestRowDto>>(
      `${REQUESTS_BASE}/symbol-request-history-for-data-table`,
      { params: input },
    );
    return data;
  },

  async getPending(): Promise<PendingSymbolRequestDto[]> {
    const { data } = await http.get<PendingSymbolRequestDto[]>(
      `${REQUESTS_BASE}/pending-symbol-requests`,
    );
    return data;
  },

  async approve(requestId: string, input: CreateGlobalSymbolDto): Promise<GlobalSymbolDto> {
    const { data } = await http.post<GlobalSymbolDto>(
      `${REQUESTS_BASE}/approve-symbol-request/${requestId}`,
      input,
    );
    return data;
  },

  async approveAll(requests: ApproveSymbolRequestDto[]): Promise<BulkApproveResultDto> {
    const { data } = await http.post<BulkApproveResultDto>(`${REQUESTS_BASE}/approve-all`, requests);
    return data;
  },

  async reject(requestId: string): Promise<boolean> {
    const { data } = await http.post<boolean>(
      `${REQUESTS_BASE}/reject-symbol-request/${requestId}`,
    );
    return data;
  },
};
